// [목적] /u/:handle/followers · /u/:handle/following — 팔로워/팔로잉 목록 페이지
// [이유] M7 P2(발견 화면 필터+비공개 프로필) — ProfilePage 팔로워/팔로잉 카운트 클릭 시 이동할 목록 화면 신설.
//         GET /users/:handle/followers·following(키셋 커서) + 행별 팔로우 토글(useFollowToggle 재사용).
//         비공개 프로필(profile_visibility='private') + 비본인 조회 시 BE 가 403 user.FORBIDDEN 반환 —
//         ErrorBoundary(CollectionPage.tsx 와 동일 관례)로 위임해 "접근 권한이 없습니다" 안내.
// [사이드 이팩트] 신규 라우트 2건(routes.tsx) — /u/:handle/followers, /u/:handle/following.
//                 useFollowToggle 은 handle 별 mutation 이라 행마다 독립 인스턴스 생성(문제 없음 — react-query
//                 mutation 은 훅 인스턴스 단위로 분리).
// [수정 시 고려사항] kind prop('followers'|'following')으로 단일 컴포넌트가 두 라우트를 겸함(중복 페이지 회피).
//                   목록 아이템은 userSnapshotSchema(경량) — bio/coverUrl 등 상세 필드 없음(user.client.ts 참조).

import { Box, ButtonBase, CircularProgress, Typography } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import { useNavigate, useParams } from 'react-router-dom';

import { TopBar } from '../components/layout/TopBar.js';
import { ErrorBoundary } from '../components/feedback/ErrorBoundary.js';
import { RowSkeleton } from '../components/feedback/Skeleton.js';
import { useShell } from '../hooks/useShell.js';
import { useAuth } from '../hooks/useAuth.js';
import { useFollowList } from '../hooks/useUserProfile.js';
import { useFollowToggle } from '../hooks/useFollowToggle.js';
import type { UserFollowPage } from '../api/user.client.js';

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const BORDER = c.line.base[L];
const PAPER0 = c.paper[0][L];
const PAPER2 = c.paper[2][L];
const BRAND = c.brand[500][L];

type FollowListKind = 'followers' | 'following';

/**
 * [목적] /u/:handle/followers 진입점 — ErrorBoundary 래핑(403 비공개 프로필 안내)
 */
export function FollowersPage(): JSX.Element {
  return <FollowListRoute kind="followers" />;
}

/**
 * [목적] /u/:handle/following 진입점 — ErrorBoundary 래핑(403 비공개 프로필 안내)
 */
export function FollowingPage(): JSX.Element {
  return <FollowListRoute kind="following" />;
}

function FollowListRoute({ kind }: { kind: FollowListKind }): JSX.Element {
  const { handle } = useParams<{ handle: string }>();
  const { status } = useAuth();

  return (
    <ErrorBoundary isGuest={status === 'guest'} loginHref={`/login?returnTo=/u/${handle}`}>
      <FollowListBody handle={handle} kind={kind} />
    </ErrorBoundary>
  );
}

function FollowListBody({
  handle,
  kind,
}: {
  handle: string | undefined;
  kind: FollowListKind;
}): JSX.Element {
  const navigate = useNavigate();
  const { openSidebar, viewport } = useShell();
  const { items, isLoading, error, hasNextPage, isFetchingNextPage, fetchNextPage } = useFollowList(
    handle,
    kind,
  );

  // [목적] 403(user.FORBIDDEN)/기타 에러를 상위 ErrorBoundary 로 위임 — CollectionPage.tsx 와 동일 관례
  // [이유] 렌더 도중 throw 해야 React 가 ErrorBoundary 로 위임함(useInfiniteQuery throwOnError:false 이므로 수동 throw)
  if (error) throw error;

  const title = kind === 'followers' ? '팔로워' : '팔로잉';

  return (
    <>
      <TopBar
        title={`@${handle ?? ''} · ${title}`}
        mobile={viewport.isMobile}
        onBurger={openSidebar}
      />
      <Box sx={{ maxWidth: 560, mx: 'auto', width: '100%', p: '20px 16px 60px' }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[...Array(4)].map((_, i) => (
              <RowSkeleton key={i} />
            ))}
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: '60px 20px' }}>
            <Typography sx={{ fontSize: 13, color: INK_MID }}>
              {kind === 'followers' ? '아직 팔로워가 없습니다.' : '아직 팔로잉이 없습니다.'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {items.map((item) => (
              <FollowRow key={item.id} item={item} onOpen={() => navigate(`/u/${item.handle}`)} />
            ))}
          </Box>
        )}

        {hasNextPage && (
          <Box sx={{ textAlign: 'center', pt: '12px' }}>
            <ButtonBase
              onClick={fetchNextPage}
              disabled={isFetchingNextPage}
              aria-label="더 불러오기"
              sx={{
                px: '20px',
                py: '8px',
                fontSize: 12,
                color: BRAND,
                border: `1px solid ${BORDER}`,
                borderRadius: `${tokens.radius.m}px`,
                '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
              }}
            >
              {isFetchingNextPage ? (
                <CircularProgress size={14} color="inherit" aria-label="불러오는 중" />
              ) : (
                '더 보기'
              )}
            </ButtonBase>
          </Box>
        )}
      </Box>
    </>
  );
}

/**
 * [목적] 팔로워/팔로잉 목록 행 — 아바타 + 이름 + handle + 팔로우 토글 버튼
 * [이유] 목록 내에서 바로 맞팔/언팔이 가능하도록 useFollowToggle(item.handle) 재사용.
 * [사이드 이팩트] 없음 — useFollowToggle 이 자체 낙관적 업데이트/Toast 를 처리.
 * [수정 시 고려사항] 본인(isFollowing 판단 불가 — 서버가 isFollowing 로 이미 계산해 줌) 행에도 버튼이 뜨는데,
 *                   자기 자신을 목록에서 클릭해 팔로우 시도 시 BE 가 400(SELF_FOLLOW_FORBIDDEN) 반환 — 허용.
 */
function FollowRow({
  item,
  onOpen,
}: {
  item: UserFollowPage['items'][number];
  onOpen: () => void;
}): JSX.Element {
  const { toggle, isPending } = useFollowToggle(item.handle);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        p: '10px 12px',
        bgcolor: PAPER0,
        border: `1px solid ${BORDER}`,
        borderRadius: `${tokens.radius.m}px`,
      }}
    >
      <ButtonBase
        onClick={onOpen}
        aria-label={`${item.displayName} 프로필 열기`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flex: 1,
          minWidth: 0,
          textAlign: 'left',
          '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
        }}
      >
        <Box
          aria-hidden={Boolean(item.avatarUrl)}
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: PAPER2,
            color: INK_HI,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 16,
            fontWeight: 700,
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          {item.avatarUrl ? (
            <Box
              component="img"
              src={item.avatarUrl}
              alt=""
              loading="lazy"
              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            item.displayName.charAt(0).toUpperCase()
          )}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: INK_HI,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.displayName}
          </Typography>
          <Typography
            sx={{
              fontSize: 11,
              color: INK_MID,
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
            }}
          >
            @{item.handle}
          </Typography>
        </Box>
      </ButtonBase>

      <ButtonBase
        onClick={() => toggle(item.isFollowing)}
        disabled={isPending}
        aria-label={item.isFollowing ? '팔로우 취소' : `${item.displayName} 팔로우`}
        aria-pressed={item.isFollowing}
        sx={{
          flexShrink: 0,
          bgcolor: item.isFollowing ? PAPER0 : BRAND,
          color: item.isFollowing ? INK_HI : PAPER0,
          border: `1px solid ${item.isFollowing ? BORDER : BRAND}`,
          borderRadius: `${tokens.radius.s}px`,
          px: '12px',
          py: '6px',
          fontSize: 11,
          fontWeight: 700,
          opacity: isPending ? 0.6 : 1,
          '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
        }}
      >
        {isPending ? (
          <CircularProgress size={11} color="inherit" aria-label="처리 중" />
        ) : item.isFollowing ? (
          '팔로우 중'
        ) : (
          '팔로우'
        )}
      </ButtonBase>
    </Box>
  );
}
